import { useMemo } from 'react';

import { emptyListMessageKey } from '../objectiveLabels';

import type { ObjectivesRead } from '@/hooks/objectives/useObjectiveListLens';
import type { TranslationKey } from '@/i18n-key';
import type { ObjectiveOrDraft, PillarSummary } from '@/types/objectives';

import { PillarScope } from '@/constants/PillarScope';
import { useObjectiveAbilities } from '@/hooks/objectives/useObjectiveAbilities';
import { useObjectiveListLens } from '@/hooks/objectives/useObjectiveListLens';
import { isPublishedObjective } from '@/utils/objectiveStatus';
import { summarizePillarAmong } from '@/utils/pillarSummariesAmong';

interface PillarPageViewParams {
    /** The pillar's objectives the reader may open, in the period of the lens. */
    objectives: ObjectiveOrDraft[];
    objectivesRead: ObjectivesRead;
    /** The pillar as the server counts it, company-wide; `undefined` until it is read. */
    companyPillar?: PillarSummary;
}

/**
 * What a pillar's page shows, read through the lens the objectives page left in the address
 * (`useObjectiveListLens`), whose parts the page reads it hands back with:
 *
 * - `heroPillar`: the header's figures, the server's under "organization", counted among the list's
 *   objectives otherwise, the way the pillar's tile counts them;
 * - `partialListCounts`: under "organization", how many of the company's objectives there are and
 *   how many the reader may open, when they may not open them all, so the list is not taken for
 *   incomplete;
 * - `emptyMessageKey`: what an empty list means to this reader.
 */
export const usePillarPageView = ({
    objectives,
    objectivesRead,
    companyPillar,
}: PillarPageViewParams) => {
    const {
        filters,
        roster,
        owners,
        statusFilter,
        pillarScope,
        heldObjectives,
        keepsDraftsOnly,
        scopedFigures,
        isTeamReach,
        filteredTeam,
    } = useObjectiveListLens(objectives, objectivesRead);
    const { hasCompanyWideReach } = useObjectiveAbilities();

    const isOrganizationScope = pillarScope.scope === PillarScope.ORGANIZATION;
    const isTeamScope = pillarScope.scope === PillarScope.TEAM;

    const heroPillar = useMemo(() => {
        if (!companyPillar || isOrganizationScope) {
            return companyPillar;
        }

        return summarizePillarAmong(companyPillar, heldObjectives);
    }, [companyPillar, isOrganizationScope, heldObjectives]);

    const visibleCount = objectives.filter(isPublishedObjective).length;
    const partialListCounts =
        isOrganizationScope &&
        !pillarScope.isResolvingScope &&
        companyPillar &&
        visibleCount < companyPillar.objectivesCount
            ? { count: companyPillar.objectivesCount, visible: visibleCount }
            : undefined;

    const resolveEmptyMessageKey = (): TranslationKey | undefined => {
        if (objectives.length > 0) {
            if (isTeamScope && heldObjectives.length === 0) {
                return 'objectives.pillarPage.emptyForTeam';
            }

            return emptyListMessageKey({
                hasObjectivesBeforeStatusFilter: owners.groups.length > 0,
                isAttributeFilterActive: roster.isFilterActive,
                isTeamReach,
            });
        }

        // A reader told that the pillar's objectives are someone else's needs no second sentence
        // saying so.
        if (partialListCounts) {
            return undefined;
        }

        return hasCompanyWideReach
            ? 'objectives.pillarPage.empty'
            : 'objectives.pillarPage.emptyWithinReach';
    };

    return {
        filters,
        roster,
        owners,
        statusFilter,
        pillarScope,
        keepsDraftsOnly,
        scopedFigures,
        filteredTeam,
        heroPillar,
        partialListCounts,
        emptyMessageKey: resolveEmptyMessageKey(),
    };
};
